/**
 * Defaults + validation for the recruiter Interview Configuration ("Interview Builder").
 * Mirrors the backend interview_configurations shape so it round-trips directly.
 *
 * The real BASE defaults (totals, duration, scoring rubric, thresholds, category split) live in the
 * backend config.py and are fetched via getInterviewDefaults() — this file only holds the *fallbacks*
 * used when that fetch fails, plus small UI helpers. It never becomes a second source of truth.
 */

import { getMetadata } from '../../../utils/encryptedStorage';

export function getCompanyId() {
  try {
    const metadata = getMetadata();
    return String(metadata?.cid || metadata?.companyId || metadata?.company_id || '0');
  } catch { return '0'; }
}

export const MODES = [
  { value: 'ai', label: 'Fully AI Generated', hint: 'The AI plans and asks every question, guided by the category mix you choose.', example: 'Best for general screening — adapts to each candidate’s resume & the JD.' },
  { value: 'recruiter', label: 'Your Questions Only', hint: 'The AI asks exactly YOUR questions, conversationally — never skipped or reworded.', example: 'Best when you have a fixed question set you must ask everyone.' },
  { value: 'hybrid', label: 'Hybrid', hint: 'Your questions + AI questions, combined in the order you pick.', example: 'Best when you have a few must-ask questions and want AI to cover the rest.' },
];

export const FOLLOWUP_MODES = [
  { value: 'strict', label: 'Strict', hint: 'No follow-ups — ask & move on' },
  { value: 'balanced', label: 'Balanced', hint: '1–2 follow-ups per answer' },
  { value: 'deep_dive', label: 'Deep Dive', hint: 'Probe each answer fully' },
];

export const PLACEMENT_STRATEGIES = [
  { value: 'sequential', label: 'Your questions first, then AI' },
  { value: 'sequential_ai_first', label: 'AI first, then your questions' },
  { value: 'distributed', label: 'Spread your questions across the AI ones' },
  { value: 'category_based', label: 'Group questions by category' },
];

// How YOUR own questions are ordered among themselves (recruiter / hybrid modes).
export const RECRUITER_ORDERS = [
  { value: 'created', label: 'As I ordered them', hint: 'Ask in the exact order listed above (drag with ↑ ↓).' },
  { value: 'category', label: 'Grouped by category', hint: 'Ask category by category, keeping your order within each.' },
  { value: 'ai', label: 'Smart order (AI flow)', hint: 'AI arranges them into a natural arc — intro → … → behavioural.' },
];

// Liveness / silence defaults (used only if the backend defaults can't be fetched).
export const SILENCE_FALLBACK = { nudge_seconds: 60, nudge_count: 2, end_after_last_nudge_seconds: 60 };

// Fallback scoring rubric — used ONLY if the backend defaults can't be fetched.
export const DEFAULT_SCORING = [
  { key: 'technical_competence', label: 'Technical Competence', weight: 30, description: '' },
  { key: 'experience_relevance', label: 'Experience Relevance', weight: 25, description: '' },
  { key: 'problem_solving', label: 'Problem Solving', weight: 20, description: '' },
  { key: 'communication', label: 'Communication', weight: 15, description: '' },
  { key: 'behavioral_fit', label: 'Behavioral & Teamwork', weight: 10, description: '' },
];

// Recommended-MINIMUM duration advisory (mirrors backend services/pacing.recommended_minimum). `cover`
// is the number of priority topics if any, ELSE the JD skills — topics OR skills, never both summed.
// `questions` counts MAIN questions (intro + resume + cover + problem-solving + behavioural); minutes
// invert the pacing model over those only, because PER_Q already budgets a question + answer + a brief
// follow-up (charging follow-ups separate slot-time inflated the estimate). Advisory only.
export function recommendedMinimum(cover) {
  const PER_Q = 2.2; const INTRO = 2.0; const CLOSING = 2.0; const BUFFER = 0.12;
  const c = Math.max(0, Number(cover) || 0);
  const questions = 1 + 1 + c + 1 + 1;
  const minutes = Math.ceil(((questions * PER_Q + INTRO + CLOSING) / Math.max(0.1, 1 - BUFFER)) / 5) * 5;
  return { questions, minutes };
}

// "problem_solving" -> "Problem Solving" (used when the registry has no explicit label).
export const humanize = (name) =>
  String(name || '').replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

export const normalizeWorkMode = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return '';
  if (text.includes('remote')) return 'remote';
  if (text.includes('hybrid')) return 'hybrid';
  if (text.includes('onsite') || text.includes('on-site') || text.includes('office')) return 'onsite';
  if (text.includes('field') || text.includes('travel')) return 'field';
  return text;
};

/** Friendly label for a category name, preferring the backend registry's label/metadata. */
export const labelFor = (name, categoryMeta = []) => {
  const m = (categoryMeta || []).find((c) => c.name === name);
  return (m && m.label) || humanize(name);
};

const sum = (arr, f) => (arr || []).reduce((a, x) => a + (Number(f(x)) || 0), 0);

export const recruiterCount = (cfg) => (cfg.custom_questions || []).length;
export const categorySum = (cfg) => sum(cfg.categories, (c) => c.question_count);
export const scoringSum = (cfg) => sum((cfg.scoring_config || {}).criteria, (c) => c.weight);

// ── builders seeded from the backend defaults (config.py base) ───────────────
// `defaults` is the `defaults` object from getInterviewDefaults(); pass null to use static fallbacks.

/** Per-job interview default (content + timing). Scoring/thresholds are inherited from the company tier. */
export function jobFromDefaults(defaults, sessionId = '') {
  const d = defaults || {};
  const sil = d.silence || SILENCE_FALLBACK;
  return {
    name: '', description: '', interview_id: sessionId || '', session_id: sessionId || '',
    mode: 'ai',
    total_questions: d.total_questions ?? 10,
    duration_minutes: d.duration_minutes ?? 20,
    question_count_mode: d.question_count_mode || 'fixed', // 'fixed' (count) | 'unrestricted' (coverage+time)
    followup_mode: d.followup_mode || 'balanced',
    placement_strategy: d.placement_strategy || 'sequential',
    recruiter_question_order: d.recruiter_question_order || 'created', // created | category | ai
    // Liveness / silence ("are you still there?") cadence — overridable per interview.
    presence_nudge_seconds: sil.nudge_seconds ?? 60,
    presence_nudge_count: sil.nudge_count ?? 2,
    presence_end_after_last_nudge_seconds: sil.end_after_last_nudge_seconds ?? 60,
    // Min interview time left for the End dialog to still offer "keep going" (rejoin).
    rejoin_min_seconds: d.rejoin_min_seconds ?? 60,
    categories: (d.category_distribution || []).map((c) => ({ ...c })), // pre-load the default mix
    custom_questions: [],
    setup_details: {
      role_title: '', role_level: '', department: '', location: '', work_mode: '',
      objective: '', skills: [], priority_topics: [], question_types: [], difficulty: '', evaluation_criteria: [],
      coding_or_task_required: false, personalization: { resume: false, jd: true },
      candidate_instructions: '',
    },
    scoring_config: {}, threshold_config: {}, // {} ⇒ inherit the company policy
  };
}

/** Company-common policy default (scoring rubric, thresholds, follow-up + fallback totals). */
export function companyFromDefaults(defaults) {
  const d = defaults || {};
  const sc = d.scoring_config && (d.scoring_config.criteria || []).length
    ? { scale_max: d.scoring_config.scale_max ?? 10, criteria: d.scoring_config.criteria.map((c) => ({ ...c })) }
    : { scale_max: 10, criteria: DEFAULT_SCORING.map((c) => ({ ...c })) };
  return {
    name: 'Company defaults', description: '', interview_id: '', session_id: '',
    mode: 'ai',
    total_questions: d.total_questions ?? 10,
    duration_minutes: d.duration_minutes ?? 20,
    question_count_mode: d.question_count_mode || 'fixed',
    placement_strategy: 'sequential',
    followup_mode: d.followup_mode || 'balanced',
    categories: [], custom_questions: [],
    scoring_config: sc,
    threshold_config: d.threshold_config || { select: 70, maybe: 50 },
  };
}

// Static fallbacks (backend defaults unavailable).
export const jobDefaults = (sessionId = '') => jobFromDefaults(null, sessionId);
export const companyDefaults = () => companyFromDefaults(null);

/** [] when valid, else human-readable error strings (drives the Save gate + inline hints). */
export function validateConfiguration(cfg, scope = 'job') {
  const errs = [];
  // Unrestricted only applies when the AI generates questions (ai/hybrid). The count-based rules below
  // are skipped — duration is the control and the AI sizes the plan to cover the role.
  const unrestricted = cfg.question_count_mode === 'unrestricted' && cfg.mode !== 'recruiter';
  if (scope === 'company') {
    const sc = scoringSum(cfg);
    if (sc !== 100) errs.push(`Scoring weights must total 100% (currently ${sc}%).`);
    if (!unrestricted && (Number(cfg.total_questions) || 0) < 1) errs.push('Default total questions must be at least 1.');
    if ((Number(cfg.duration_minutes) || 0) < 15) errs.push('Default duration must be at least 15 minutes.');
    return errs;
  }
  // per-job (session) — scoring/thresholds/follow-up are inherited from the company tier.
  const total = Number(cfg.total_questions) || 0;
  if (!String(cfg.name || '').trim()) errs.push('Interview name is required.');
  if (!String(cfg.session_id || '').trim()) errs.push('Job / Session ID is required.');
  if (!unrestricted && total < 1) errs.push('Total questions must be at least 1.');
  if ((Number(cfg.duration_minutes) || 0) < 15) errs.push('Duration must be at least 15 minutes.');
  const details = cfg.setup_details || {};
  if (!String(details.objective || '').trim()) errs.push('Interview objective is required.');
  if (!Array.isArray(details.skills) || details.skills.length === 0) errs.push('Add at least one skill to evaluate.');
  if (!Array.isArray(details.question_types) || details.question_types.length === 0) errs.push('Add at least one question type.');
  if (!String(details.difficulty || '').trim()) errs.push('Difficulty level is required.');
  if (!Array.isArray(details.evaluation_criteria) || details.evaluation_criteria.length === 0) errs.push('Add at least one evaluation criterion.');
  if (cfg.mode === 'ai') {
    // empty categories in AI mode = "let the AI decide" — valid; only validate a custom mix (fixed only).
    if (!unrestricted && categorySum(cfg) > 0 && categorySum(cfg) !== total) {
      errs.push(`Category counts must total ${total} (currently ${categorySum(cfg)}).`);
    }
  } else if (cfg.mode === 'recruiter') {
    if (recruiterCount(cfg) !== total) errs.push(`Add exactly ${total} question(s) (currently ${recruiterCount(cfg)}).`);
    (cfg.custom_questions || []).forEach((q, i) => {
      if (!String(q.question_text || '').trim()) errs.push(`Question ${i + 1} is empty.`);
    });
  } else if (cfg.mode === 'hybrid') {
    const cs = categorySum(cfg);
    const rc = recruiterCount(cfg);
    if (rc < 1) errs.push('Add at least one of your own questions for hybrid mode.');
    if (!unrestricted) {
      if (cs === 0) {
        // no explicit AI mix → the AI fills the remaining (total − your questions) freely.
        if (rc > total) errs.push(`You have ${rc} question(s) but the total is ${total}.`);
      } else if (rc + cs !== total) {
        errs.push(`Your questions (${rc}) + AI categories (${cs}) must total ${total}.`);
      }
    }
    (cfg.custom_questions || []).forEach((q, i) => {
      if (!String(q.question_text || '').trim()) errs.push(`Your question ${i + 1} is empty.`);
    });
  }
  return errs;
}
